import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';

interface TestResult {
  success: boolean;
  results?: any;
  error?: string;
}

export const PMT2PathTestSection: React.FC = () => {
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [migrationResult, setMigrationResult] = useState<TestResult | null>(null);
  const [loading, setLoading] = useState(false);

  const functions = getFunctions();

  const testPathSanitization = async () => {
    setLoading(true);
    setTestResult(null);
    
    try {
      const testFunction = httpsCallable(functions, 'testPathSanitization');
      const result = await testFunction();
      
      setTestResult({
        success: true,
        results: result.data
      });
    } catch (error: any) {
      setTestResult({
        success: false,
        error: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const runMigrationDryRun = async () => {
    setLoading(true);
    setMigrationResult(null);
    
    try {
      const migrationFunction = httpsCallable(functions, 'pmt2PathMigration');
      const result = await migrationFunction({
        dryRun: true,
        specificCompany: "Gold Label Group 2",
        batchSize: 10
      });
      
      setMigrationResult({
        success: true,
        results: result.data
      });
    } catch (error: any) {
      setMigrationResult({
        success: false,
        error: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>PMT2 Path Length Fix Testing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          
          {/* Test Path Sanitization */}
          <div>
            <Button 
              onClick={testPathSanitization}
              disabled={loading}
              className="mb-4"
            >
              {loading ? 'Testing...' : 'Test Path Sanitization'}
            </Button>
            
            {testResult && (
              <Alert className={testResult.success ? 'border-green-500' : 'border-red-500'}>
                <AlertDescription>
                  {testResult.success ? (
                    <div>
                      <strong>✅ Sanitization Test Passed!</strong>
                      <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto">
                        {JSON.stringify(testResult.results, null, 2)}
                      </pre>
                    </div>
                  ) : (
                    <div>
                      <strong>❌ Test Failed:</strong> {testResult.error}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Migration Dry Run */}
          <div>
            <Button 
              onClick={runMigrationDryRun}
              disabled={loading}
              variant="outline"
              className="mb-4"
            >
              {loading ? 'Running...' : 'Run Migration Dry Run (Gold Label Group 2)'}
            </Button>
            
            {migrationResult && (
              <Alert className={migrationResult.success ? 'border-green-500' : 'border-red-500'}>
                <AlertDescription>
                  {migrationResult.success ? (
                    <div>
                      <strong>✅ Dry Run Completed!</strong>
                      <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-64">
                        {JSON.stringify(migrationResult.results, null, 2)}
                      </pre>
                    </div>
                  ) : (
                    <div>
                      <strong>❌ Dry Run Failed:</strong> {migrationResult.error}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <Alert>
            <AlertDescription>
              <strong>Next Steps:</strong>
              <ol className="list-decimal list-inside mt-2 space-y-1">
                <li>First, test path sanitization to verify the logic works</li>
                <li>Run the dry run to see what would be migrated</li>
                <li>If dry run looks good, contact support to run live migration</li>
              </ol>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};